import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Link,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type { DashboardAppraisal, DashboardPastEvaluation } from '@/api/dashboardApi';
import { RATING_LABELS } from '@/utils/ratingLabels';
import type { SkillRatingScale } from '@/api/types/jobEnd.types';
import { toSimplifiedRating } from '@/api/types/jobEnd.types';
import { Section, SectionCard, SectionHeader } from '@/components/common';
import { formatDateShort } from '@/utils/dateFormatter';

type SortDirection = 'asc' | 'desc';

const RATING_VISIBILITY_KEY = 'pastEvaluations.ratingVisible';

function formatDateRange(startDate: string, endDate: string | null): string {
  const start = formatDateShort(startDate);
  if (!endDate) return `${start} - `;
  return `${start} - ${formatDateShort(endDate)}`;
}

function getRatingLabel(rating: string | null, visible: boolean): string {
  if (!visible || !rating) return '-';
  return RATING_LABELS[rating as SkillRatingScale] ?? '-';
}

function getSimplifiedRatingLabel(rating: string | null, visible: boolean): string {
  if (!visible || !rating) return '-';
  return toSimplifiedRating(rating as SkillRatingScale);
}

interface MyPastEvaluationSectionProps {
  pastEvaluations: DashboardPastEvaluation[];
  appraisals: DashboardAppraisal[];
  showPastAppraisals?: boolean;
}

export function MyPastEvaluationSection({ pastEvaluations, appraisals, showPastAppraisals = true }: MyPastEvaluationSectionProps) {
  const [ratingVisible, setRatingVisible] = useState(() => {
    const stored = localStorage.getItem(RATING_VISIBILITY_KEY);
    return stored === 'true';
  });

  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [appraisalSortDir, setAppraisalSortDir] = useState<SortDirection>('desc');

  const sortedEvaluations = useMemo(() => {
    return [...pastEvaluations].sort((a, b) => {
      const dateA = new Date(a.assignmentEndDate ?? a.assignmentStartDate).getTime();
      const dateB = new Date(b.assignmentEndDate ?? b.assignmentStartDate).getTime();
      return sortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [pastEvaluations, sortDir]);

  const sortedAppraisals = useMemo(() => {
    return [...appraisals].sort((a, b) => {
      const dateA = new Date(a.cycleEndDate).getTime();
      const dateB = new Date(b.cycleEndDate).getTime();
      return appraisalSortDir === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [appraisals, appraisalSortDir]);

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setRatingVisible(newValue);
    localStorage.setItem(RATING_VISIBILITY_KEY, String(newValue));
  };

  return (
    <Section>
      <SectionHeader
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">Rating Visibility</Typography>
            <Switch
              checked={ratingVisible}
              onChange={handleToggle}
              size="small"
              sx={{ '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.3)' } }}
            />
          </Box>
        }
      >
        MY PAST EVALUATIONS
      </SectionHeader>
      <Grid container spacing={{ xs: 1 }} sx={{ alignItems: 'stretch' }}>
        <Grid size={{ xs: 12, md: showPastAppraisals ? 6 : 12 }} sx={{ minWidth: 0, display: 'flex' }}>
          <SectionCard>
            <Typography component="h3" sx={{ typography: { xs: 'h6', md: 'h5' }, mb: 2 }}>
              My Job Ends
            </Typography>
            {pastEvaluations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No past evaluations to display.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'custom.darkTeal', '& th': { color: 'common.white' } }}>
                      <TableCell>PROJECT NAME (CODE)</TableCell>
                      <TableCell>
                        <TableSortLabel
                          active
                          direction={sortDir}
                          onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                          sx={{
                            color: 'inherit !important',
                            '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                          }}
                        >
                          DATES
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>TENURE IN GRADE</TableCell>
                      <TableCell>RATING</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedEvaluations.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Link
                            component={RouterLink}
                            to={`/job-ends/${item.id}`}
                            underline="hover"
                          >
                            {item.projectName} ({item.projectCode})
                          </Link>
                        </TableCell>
                        <TableCell>
                          {formatDateRange(item.assignmentStartDate, item.assignmentEndDate)}
                        </TableCell>
                        <TableCell>{item.tenureInGrade ?? '-'}</TableCell>
                        <TableCell>{getSimplifiedRatingLabel(item.overallRating, ratingVisible)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SectionCard>
        </Grid>
        {showPastAppraisals && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0, display: 'flex' }}>
            <SectionCard>
              <Typography component="h3" sx={{ typography: { xs: 'h6', md: 'h5' }, mb: 2 }}>
                My Appraisals
              </Typography>
              {appraisals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No appraisals to display.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'custom.darkTeal', '& th': { color: 'common.white' } }}>
                        <TableCell>
                          <TableSortLabel
                            active
                            direction={appraisalSortDir}
                            onClick={() => setAppraisalSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                            sx={{
                              color: 'inherit !important',
                              '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                            }}
                          >
                            PERIOD
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>TENURE IN GRADE</TableCell>
                        <TableCell>RATING</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedAppraisals.map((appraisal) => (
                        <TableRow key={appraisal.id}>
                          <TableCell>
                            <Link
                              component={RouterLink}
                              to={`/appraisals/${appraisal.id}`}
                              underline="hover"
                            >
                              {appraisal.cycleName}
                            </Link>
                          </TableCell>
                          <TableCell>{appraisal.tenureInGrade ?? '-'}</TableCell>
                          <TableCell>{getRatingLabel(appraisal.rating, ratingVisible)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </SectionCard>
          </Grid>
        )}
      </Grid>
    </Section>
  );
}

export default MyPastEvaluationSection;
